import {applyMiddleware, compose, createStore} from 'redux';
import thunk from 'redux-thunk';
import reduceReducers from 'reduce-reducers';
// utils
import persistInLocalStorage, {
  localStorageAvailable
} from '~/utils/persistInLocalStorage';
import actionRecorder from '~/utils/middleware/actionRecorder';
import eventTracker from '~/utils/middleware/eventTracker';
import confirmationHandler from '~/utils/middleware/confirmationHandler';
import errorReporter from '~/utils/middleware/errorReporter';
import realTimePriceUpdater from '~/utils/middleware/realTimePriceUpdater';
import selectionAnalyzer from '~/utils/middleware/selectionAnalyzer';
import recentUploadsTabSync from '~/utils/middleware/recentUploadsTabSync';
import undoMiddleware from '~/utils/middleware/undo';
import {isInternal} from '~/utils/user';
import {isValidData} from '~/utils/localStorage';
// serializers
import {serialize} from '~/data/serializers/localStorage';
import {deserialize} from '~/data/deserializers/localStorage';
// constants
import LocalStorageConstants from '~/constants/LocalStorageConstants';
import {DATA_MODEL_VERSION} from '~/constants/App';
// types
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import ArtTypes from '~/constants/types/ArtTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import FontTypes from '~/constants/types/FontTypes';
import KeyboardTypes from '~/constants/types/KeyboardTypes';
import NotificationTypes from '~/constants/types/NotificationTypes';
import TextTypes from '~/constants/types/TextTypes';
// reducers
import combineReducers from '~/utils/combineReducers';
import * as coreReducers from '~/reducers/core';
import * as reducers from '~/reducers';
import complexReducers from '~/reducers/complex';

// api & slices
import {designsApi} from '~/api/designs';
import {mmsApi} from '~/api/mms';
import {priceQuoteApi} from '~/api/quote';
import {quoteWidgetApi} from '~/api/quoteWidget';
import cartReducer from '~/slices/cartSlice';
import modalReducer from '~/slices/modalSlice';
import pathReducer from '~/slices/pathSlice';
import priceReducer from '~/slices/priceSlice';
import metricsReducer from '~/slices/metricsSlice';
import rollbar from './vendor/rollbar';

var analyticsTypeStrings = Object.values(AnalyticsTypes);
var keyboardTypeStrings = Object.values(KeyboardTypes);
var notificationTypeStrings = Object.values(NotificationTypes);
var primaryBlackList = analyticsTypeStrings
  .concat(keyboardTypeStrings)
  .concat(notificationTypeStrings)
  .concat([
    ArtTypes.LOADING_RESULTS,
    ArtTypes.CLEAR,
    DesignTypes.SAVE,
    DesignTypes.FETCH,
    FontTypes.LOADING_RESULTS
  ]);

var persistanceBlackList = primaryBlackList.concat([
  ArtTypes.ADD_ART,
  TextTypes.ADD_TEXT,
  TextTypes.UPDATE_TEXT,
  TextTypes.UPDATE_FONT
]);

var rollbarBlackList = primaryBlackList
  .concat(notificationTypeStrings)
  .concat([CanvasTypes.ICON_RENDERED]);

var storeEnhancers = [
  applyMiddleware(
    thunk,
    designsApi.middleware,
    mmsApi.middleware,
    priceQuoteApi.middleware,
    quoteWidgetApi.middleware,
    actionRecorder(rollbarBlackList),
    eventTracker,
    selectionAnalyzer,
    realTimePriceUpdater,
    errorReporter,
    confirmationHandler,
    recentUploadsTabSync,
    undoMiddleware
  )
];

const localStoragePossible = localStorageAvailable() && !isInternal();

if (localStoragePossible) {
  storeEnhancers.push(
    persistInLocalStorage({
      key: LocalStorageConstants.SNAPSHOT,
      timestamp: Date.now(),
      versionKey: LocalStorageConstants.VERSION,
      version: DATA_MODEL_VERSION,
      deserialize,
      serialize,
      readError: error => {
        rollbar.error('Local Storage read error:', error);
      },
      writeError: error => {
        rollbar.error('Local Storage write error:', error);
      },
      persistanceBlackList,
      isValidData
    })
  );
}

if (window.__REDUX_DEVTOOLS_EXTENSION__) {
  const actionSanitizer = action => {
    return action.type === 'assistedDesign/FETCH_SUCCESS'
      ? {...action, payload: '<<LONG_BLOB>>'}
      : action;
  };

  const stateSanitizer = state => {
    if (state.icon || state.design) {
      return {
        ...state,
        design: state.design.present.slice(0, 3),
        icon: state.icon.present.slice(0, 10)
      };
    }

    return state;
  };

  storeEnhancers.push(
    window.__REDUX_DEVTOOLS_EXTENSION__({
      // latency: 500,
      // maxAge: 10,
      trace: true,
      actionSanitizer,
      stateSanitizer
    })
  );
}

const finalCreateStore = compose(...storeEnhancers)(createStore);

const sliceReducers = combineReducers({
  ...coreReducers,
  ...reducers,
  cart: cartReducer,
  modal: modalReducer,
  path: pathReducer,
  price: priceReducer,
  metrics: metricsReducer,
  [designsApi.reducerPath]: designsApi.reducer,
  [mmsApi.reducerPath]: mmsApi.reducer,
  [priceQuoteApi.reducerPath]: priceQuoteApi.reducer,
  [quoteWidgetApi.reducerPath]: quoteWidgetApi.reducer
});
export const rootReducer = reduceReducers(sliceReducers, ...complexReducers);

export default function configureStore() {
  return finalCreateStore(rootReducer);
}
